import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Mail,
  Phone,
  MapPin,
  Github,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";

interface SiteSettings {
  [key: string]: string;
}

const Footer: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [activeToastId, setActiveToastId] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get("/settings");
        setSettings(response.data.data || response.data);
      } catch (error) {
        console.error("Failed to fetch site settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleContactClick = () => {
    // If there's already an active toast, don't create a new one
    if (activeToastId) {
      return;
    }

    const email = settings.contact_email || settings.support_email;
    const phone = settings.phone_primary || settings.phone_secondary;

    let message = "You can reach us at:\n\n";
    if (email) message += `📧 Email: ${email}\n`;
    if (phone) message += `📞 Phone: ${phone}`;

    const toastId = toast.success(message, {
      duration: 6000,
      style: {
        minWidth: "300px",
        whiteSpace: "pre-line",
      },
    });

    setActiveToastId(toastId);

    // Clear the active toast ID after it auto-dismisses
    setTimeout(() => {
      setActiveToastId(null);
    }, 6000);
  };
  return (
    <footer className="bg-gray-900 dark:bg-gray-950 text-white border-t border-gray-800 dark:border-gray-900">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="text-white w-6 h-6" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Swags29
              </span>
            </div>
            <p className="text-gray-300 mb-6 max-w-md leading-relaxed">
              {loading
                ? "Empowering students worldwide with personalized English learning experiences."
                : settings.about_subtitle ||
                  "Empowering students worldwide with personalized English learning experiences."}
            </p>
            <div className="flex space-x-4">
              {settings.github_url && settings.github_url.trim() !== "" && (
                <a
                  href={settings.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Github className="w-5 h-5" />
                </a>
              )}
              {settings.linkedin_url && settings.linkedin_url.trim() !== "" && (
                <a
                  href={settings.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {settings.twitter_url && settings.twitter_url.trim() !== "" && (
                <a
                  href={settings.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {settings.facebook_url && settings.facebook_url.trim() !== "" && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings.instagram_url &&
                settings.instagram_url.trim() !== "" && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
              {settings.youtube_url && settings.youtube_url.trim() !== "" && (
                <a
                  href={settings.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 dark:bg-gray-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/"
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/packages"
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  Packages
                </a>
              </li>
              <li>
                <a
                  href="/dashboard"
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  Dashboard
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  About Us
                </a>
              </li>
              <li>
                <button
                  onClick={handleContactClick}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Info</h3>
            <ul className="space-y-4">
              {(settings.contact_email || settings.support_email) && (
                <li className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <a
                    href={`mailto:${
                      settings.contact_email || settings.support_email
                    }`}
                    className="text-gray-300 hover:text-blue-400 transition-colors"
                  >
                    {settings.contact_email || settings.support_email}
                  </a>
                </li>
              )}
              {(settings.phone_primary || settings.phone_secondary) && (
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-blue-400" />
                  <a
                    href={`tel:${
                      settings.phone_primary || settings.phone_secondary
                    }`}
                    className="text-gray-300 hover:text-blue-400 transition-colors"
                  >
                    {settings.phone_primary || settings.phone_secondary}
                  </a>
                </li>
              )}
              {settings.address && (
                <li className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-300">{settings.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="py-8 border-t border-gray-800 dark:border-gray-900">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                Disclaimer: This Website is a EdTech solution. References:
                IELTS, Zoom. All trademarks belong to their respective owners.
              </p>
              <p className="text-gray-500 mt-2 text-sm">
                © 2026 Swags29. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookie-policy"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
